// pages/Exhibition.jsx
import React, { useEffect, useState } from 'react';
import { apiGet } from '../api/client';

const formatDate = (value) =>
    new Date(value).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const formatDateRange = (startDate, endDate, timings) => {
    let range = formatDate(startDate);
    if (endDate) {
        range += ` - ${formatDate(endDate)}`;
    }
    if (timings) {
        range += ` (${timings})`;
    }
    return range;
};

const Exhibition = () => {
    const [currentExhibitions, setCurrentExhibitions] = useState([]);
    const [pastExhibitions, setPastExhibitions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadExhibitions = async () => {
            try {
                const data = await apiGet('/exhibitions');
                setCurrentExhibitions(data.filter((item) => item.status === 'current'));
                setPastExhibitions(data.filter((item) => item.status === 'past'));
            } catch (err) {
                setError('Unable to load exhibitions right now.');
            } finally {
                setLoading(false);
            }
        };

        loadExhibitions();
    }, []);

    return (
        <div className="container">
            <div className="section-header" style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <h2 className="section-title">Exhibitions</h2>
                <p style={{ maxWidth: '800px', margin: '0 auto', fontSize: '1.1rem' }}>
                    Explore Samir Dutta's exhibition history and current showcases.
                </p>
            </div>

            {loading && (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                    <p>Loading exhibitions...</p>
                </div>
            )}

            {error && (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                    <p>{error}</p>
                </div>
            )}

            {!loading && !error && (
                <>
                    {/* Current Exhibition Section */}
                    {currentExhibitions.length > 0 && (
                        <section className="section">
                            <h3 style={{
                                fontSize: '1.8rem',
                                marginBottom: '1.5rem',
                                color: 'var(--primary-color)',
                                textAlign: 'center'
                            }}>
                                Current Exhibition
                            </h3>

                            {currentExhibitions.map((exhibition) => (
                                <div key={exhibition.id} className="current-exhibition slide-up" style={{
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.1)',
                                    marginBottom: '3rem'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                    }}>
                                        <div style={{
                                            flex: '1',
                                            minWidth: '300px',
                                            padding: '2rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}>
                                            <h3 style={{
                                                fontSize: '1.8rem',
                                                color: 'var(--primary-color)',
                                                marginBottom: '0.5rem'
                                            }}>
                                                {exhibition.title}
                                            </h3>
                                            <p style={{
                                                fontSize: '1.2rem',
                                                marginBottom: '0.5rem',
                                                color: 'var(--accent-color)'
                                            }}>
                                                {exhibition.venue}, {exhibition.location}
                                            </p>
                                            <p style={{
                                                marginBottom: '1rem',
                                                fontWeight: 'bold'
                                            }}>
                                                {formatDateRange(exhibition.startDate, exhibition.endDate, exhibition.timings)}
                                            </p>
                                            <p>
                                                {exhibition.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </section>
                    )}

                    {/* Past Exhibitions */}
                    <section className="section">
                        <h3 style={{
                            fontSize: '1.8rem',
                            marginBottom: '1.5rem',
                            color: 'var(--primary-color)',
                            textAlign: 'center'
                        }}>
                            Past Exhibitions
                        </h3>

                        {/* Past exhibitions grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '1.5rem'
                        }}>
                            {pastExhibitions.map((exhibition) => (
                                <div key={exhibition.id} className="past-exhibition slide-up" style={{
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
                                    height: '100%'
                                }}>
                                    <div style={{ height: '200px' }}>
                                        <img
                                            src={exhibition.imageUrl}
                                            alt={exhibition.title}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    </div>
                                    <div style={{ padding: '1.5rem' }}>
                                        <h4 style={{
                                            color: 'var(--primary-color)',
                                            marginBottom: '0.5rem'
                                        }}>
                                            {exhibition.title}
                                        </h4>
                                        <p style={{
                                            marginBottom: '0.5rem',
                                            color: 'var(--accent-color)'
                                        }}>
                                            {exhibition.venue}, {exhibition.location}
                                        </p>
                                        <p>
                                            {formatDateRange(exhibition.startDate, exhibition.endDate, exhibition.timings)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {pastExhibitions.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                                <p>No past exhibitions to show yet.</p>
                            </div>
                        )}
                    </section>
                </>
            )}

            {/* International Presence - Simplified */}
            <section className="section" style={{
                textAlign: 'center',
                margin: '0 0 2rem',
                padding: '2rem',
                backgroundColor: 'rgba(227, 180, 72, 0.05)',
                borderRadius: '8px'
            }}>
                <h3 style={{ color: 'var(--primary-color)', marginBottom: '1rem' }}>International Presence</h3>
                <p>Samir Dutta's work has been featured in galleries and exhibitions in:</p>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '2rem',
                    margin: '1.5rem 0',
                    flexWrap: 'wrap'
                }}>
                    <div>
                        <i className="fas fa-map-marker-alt" style={{ color: 'var(--primary-color)', marginRight: '0.5rem' }}></i>
                        Bangladesh
                    </div>
                    <div>
                        <i className="fas fa-map-marker-alt" style={{ color: 'var(--primary-color)', marginRight: '0.5rem' }}></i>
                        Amsterdam, Holland
                    </div>
                    <div>
                        <i className="fas fa-map-marker-alt" style={{ color: 'var(--primary-color)', marginRight: '0.5rem' }}></i>
                        Washington D.C., USA
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Exhibition;
